<template>
  <standalone-call
    v-if="isCallFormOpen"
    @close="closeCallForm"
    @call:start="handleCall"
  />
  <teleport v-if="callDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="closeCallDialog"
    />
  </teleport>
  <send-deal-communication-modal
    v-if="communicationModalType"
    :type="communicationModalType"
    standalone
    anchor-bottom-left
    @close-dialog="closeCommunicationModal"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { CommunicationProvider } from "@/enums/communicationProviders";
import { setActiveCallSession } from "@/helpers/communication";
import { TWILIO_PIP_OPEN } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import { useContacts } from "@/hooks/contacts";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCallsStore } from "@/stores/calls";
import { useDialerStore } from "@/stores/dialer";
import useQuickActionsStore from "@/stores/quickActions";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import StandaloneCall from "@/components/communication/standalone/StandaloneCall.vue";
import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";

const { trackEvent } = useAnalyticsStore();
const quickActionsStore = useQuickActionsStore();
const callsStore = useCallsStore();
const dialerApi = useDialerStore();
const { effectiveDialer } = useTwilio();
const { callClientStandalone } = useContacts();
const emitter = useEmitter();

const { communicationModalType, isCallFormOpen, callDialog } =
  storeToRefs(quickActionsStore);
const {
  closeCommunicationModal,
  closeCallForm,
  openCallDialog,
  closeCallDialog,
} = quickActionsStore;
const { isPiPOpen } = storeToRefs(callsStore);

const handleCall = async (phoneNumber: string) => {
  if (callsStore.isCallActive) {
    return;
  }

  const result = await callClientStandalone({
    to: phoneNumber,
  });

  if (!result) {
    return;
  }

  const resolvedProvider =
    result.callOutPayload?.provider ?? effectiveDialer.value;

  trackEvent(AnalyticsEvent.QuickActionsCallInitiated, {
    provider: resolvedProvider,
  });
  closeCallForm();
  setActiveCallSession(result, effectiveDialer.value);

  if (
    resolvedProvider === CommunicationProvider.Twilio &&
    !isPiPOpen.value &&
    dialerApi.isActiveDialer
  ) {
    emitter.emit(TWILIO_PIP_OPEN);
  }

  // Suppress the wrap-up dialog when the desktop dialer owns the call —
  // it has its own wrap-up screen and we don't want duplicate writes.
  if (dialerApi.isActiveDialer) {
    openCallDialog({ log: result.connectionLog });
  }
};
</script>
